import type { CommandBatch } from '../editor/commandHistory.js';
import { interactionState, pan } from './viewportState.js';
import { moveNodeLabel } from '../editor/historyLabels.js';
import type { PendingConnection } from '../editor/connectionMutations.js';
import type { NodeEditorController } from './nodeEditor.js';
import type { ConnectionStateController } from './connectionState.js';

const SNAP_RADIUS = 28;

interface NodeInteractionDeps {
  commandBatch: CommandBatch;
  connectionState: ConnectionStateController;
  viewState: NodeInteractionViewState;
  moveNode: (nodeId: string, x: number, y: number) => void;
  nodeEditor: Pick<NodeEditorController, 'renderGraph' | 'updateNodePositions' | 'updatePendingConnection'>;
}

export interface NodeInteractionViewState {
  recordMousePosition(e: MouseEvent): void;
  restoreSnapHighlights(connectionState: ConnectionStateController): void;
  restoreHoverState(connectionState: ConnectionStateController): void;
}

function findSnapTarget(screenX: number, screenY: number, pc: PendingConnection): HTMLElement | null {
  const wantedIo = pc.isFromOut ? 'in' : 'out';
  let best: HTMLElement | null = null;
  let bestDist = SNAP_RADIUS;
  document.querySelectorAll<HTMLElement>(`.port-dot[data-io="${wantedIo}"]`).forEach(dot => {
    const r = dot.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    const d = Math.hypot(screenX - cx, screenY - cy);
    if (d < bestDist) {
      bestDist = d;
      best = dot;
    }
  });
  return best;
}

export function clearSnapHighlights(): void {
  document.querySelectorAll('.port-dot.snap-target').forEach(dot => dot.classList.remove('snap-target'));
}

export function createNodeInteractionViewState(): NodeInteractionViewState {
  const lastMouse = { x: 0, y: 0 };

  return {
    recordMousePosition(e: MouseEvent): void {
      lastMouse.x = e.clientX;
      lastMouse.y = e.clientY;
    },
    restoreSnapHighlights(connectionState: ConnectionStateController): void {
      clearSnapHighlights();
      const pendingConn = connectionState.pendingConn;
      if (!pendingConn) return;
      const snap = findSnapTarget(lastMouse.x, lastMouse.y, pendingConn);
      if (snap) snap.classList.add('snap-target');
    },
    restoreHoverState(connectionState: ConnectionStateController): void {
      document.querySelectorAll('.port-dot.hovered').forEach(dot => dot.classList.remove('hovered'));
      if (connectionState.pendingConn) return;
      const els = document.elementsFromPoint(lastMouse.x, lastMouse.y);
      for (const el of els) {
        if (el instanceof HTMLElement && el.classList.contains('port-dot')) {
          el.classList.add('hovered');
          break;
        }
      }
    },
  };
}

export function initNodeInteractions(graphArea: HTMLElement, deps: NodeInteractionDeps): void {
  window.addEventListener('mousemove', e => {
    deps.viewState.recordMousePosition(e);
  }, true);

  function finishNodeDrag(commit: boolean): void {
    if (!interactionState.draggingNode) return;
    if (interactionState.draggingNode.moved) {
      if (commit) deps.commandBatch.commit();
      else deps.commandBatch.discard();
    }
    interactionState.draggingNode = null;
  }

  window.addEventListener('mousemove', e => {
    document.querySelectorAll('.port-dot.hovered').forEach(dot => dot.classList.remove('hovered'));

    if (interactionState.draggingNode) {
      const dx = (e.clientX - interactionState.draggingNode.startX) / pan.scale;
      const dy = (e.clientY - interactionState.draggingNode.startY) / pan.scale;
      if (!interactionState.draggingNode.moved && (dx !== 0 || dy !== 0)) {
        deps.commandBatch.begin(moveNodeLabel(interactionState.draggingNode.node.type));
        interactionState.draggingNode.moved = true;
      }
      if (interactionState.draggingNode.moved) {
        const nextX = Math.round(interactionState.draggingNode.origX + dx);
        const nextY = Math.round(interactionState.draggingNode.origY + dy);
        deps.moveNode(interactionState.draggingNode.node.id, nextX, nextY);
        deps.nodeEditor.updateNodePositions();
      }
      return;
    }

    const pendingConn = deps.connectionState.pendingConn;
    if (pendingConn) {
      const rect = graphArea.getBoundingClientRect();
      pendingConn.current = {
        x: (e.clientX - rect.left - pan.x) / pan.scale,
        y: (e.clientY - rect.top - pan.y) / pan.scale
      };
      if (pendingConn.startScreen) {
        const dist = Math.hypot(e.clientX - pendingConn.startScreen.x, e.clientY - pendingConn.startScreen.y);
        if (dist > 4) {
          pendingConn.suppressMouseUp = false;
          pendingConn.ignoreNextClick = false;
          pendingConn.startScreen = undefined;
        }
      }
      clearSnapHighlights();
      const snap = findSnapTarget(e.clientX, e.clientY, pendingConn);
      if (snap) snap.classList.add('snap-target');
      deps.nodeEditor.updatePendingConnection();
    }
  });

  window.addEventListener('mouseup', e => {
    finishNodeDrag(true);

    const pendingConn = deps.connectionState.pendingConn;
    if (!pendingConn) return;
    if (pendingConn.suppressMouseUp) return;

    clearSnapHighlights();
    let targetDot = findSnapTarget(e.clientX, e.clientY, pendingConn);
    if (!targetDot) {
      const el = e.target as HTMLElement;
      if (el.classList && el.classList.contains('port-dot')) targetDot = el;
    }

    if (targetDot) {
      const io = targetDot.dataset.io!;
      const nodeId = targetDot.dataset.node!;
      const portIdx = parseInt(targetDot.dataset.port!);
      const result = deps.connectionState.completePendingConnection(nodeId, portIdx, io as 'in' | 'out');
      if (result.connected || result.typeMismatch) {
        deps.nodeEditor.renderGraph();
      }
    }

    if (deps.connectionState.pendingConn) {
      deps.connectionState.clearPendingConn();
      deps.nodeEditor.renderGraph();
    }
  });

  window.addEventListener('blur', () => {
    finishNodeDrag(true);
  });
}
